import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

export default function CatalogManager() {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', position: 0, is_active: true });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchCatalogs = useCallback(() => {
    api.get('/catalogs').then(r => setCatalogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchCatalogs(); }, [fetchCatalogs]);

  const resetForm = () => {
    setForm({ title: '', position: 0, is_active: true });
    setThumbnailFile(null);
    setPdfFile(null);
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (catalog) => {
    setEditing(catalog);
    setForm({ title: catalog.title || '', position: catalog.position || 0, is_active: catalog.is_active !== false });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Başlık gerekli'); return; }
    setIsSubmitting(true);
    try {
      let thumbnailPath = editing?.thumbnail_path || '';
      let pdfPath = editing?.pdf_path || '';
      
      if (thumbnailFile) {
        const fd = new FormData(); fd.append('file', thumbnailFile);
        const res = await api.post('/uploads', fd);
        thumbnailPath = res.data.path;
      }
      if (pdfFile) {
        const fd = new FormData(); fd.append('file', pdfFile);
        const res = await api.post('/uploads', fd);
        pdfPath = res.data.path;
      }

      const payload = { ...form, thumbnail_path: thumbnailPath, pdf_path: pdfPath };
      if (editing) {
        await api.put(`/catalogs/${editing.id}`, payload);
        toast.success('Katalog güncellendi');
      } else {
        await api.post('/catalogs', payload);
        toast.success('Katalog eklendi');
      }
      resetForm();
      fetchCatalogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/catalogs/${id}`);
      toast.success('Katalog silindi');
      fetchCatalogs();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  return (
    <div data-testid="admin-catalog-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">E-Katalog Yönetimi</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors"
        >
          <Plus className="w-4 h-4" /> Yeni Katalog
        </button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Katalog Duzenle' : 'Yeni Katalog'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Başlık <span className="text-destructive">*</span></label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Sıra</label>
                <input type="number" value={form.position} onChange={e => setForm(f => ({ ...f, position: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Önizleme Görseli</label>
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary transition-colors">
                  <Upload className="w-4 h-4" /> Görsel Seç
                  <input type="file" accept="image/*" className="hidden" onChange={e => setThumbnailFile(e.target.files[0])} />
                </label>
                <span className="text-xs text-muted-foreground ml-2">{thumbnailFile?.name || ''}</span>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">PDF Dosyası</label>
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary transition-colors">
                  <Upload className="w-4 h-4" /> PDF Sec
                  <input type="file" accept=".pdf" className="hidden" onChange={e => setPdfFile(e.target.files[0])} />
                </label>
                <span className="text-xs text-muted-foreground ml-2">{pdfFile?.name || ''}</span>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
              Aktif
            </label>

            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50" data-testid="admin-save-button">
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : catalogs.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">Henüz katalog eklenmemiş</div>
      ) : (
        <div className="space-y-3">
          {catalogs.map(c => (
            <div key={c.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-12 h-16 rounded-lg overflow-hidden bg-secondary shrink-0 flex items-center justify-center">
                {c.thumbnail_path ? <img src={getFileUrl(c.thumbnail_path)} alt="" className="w-full h-full object-cover" /> : <FileText className="w-5 h-5 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{c.title}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(c)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(c.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
